// ============================================================
// FORECAST MODULE — Public API
//
// Re-exports everything needed by the ForecastPage component.
// Import from this barrel file instead of individual modules.
//
// Architecture:
//   forecast/
//   ├── types.ts              — All shared type definitions
//   ├── constants.ts          — Evolution configs, colors, platform lists
//   ├── math.ts               — Deterministic random, normal distribution
//   ├── adaptiveJitter.ts     — Data-driven noise profiling
//   ├── roasForecastEngine.ts — Paid ROAS forecast pipeline
//   ├── nonPaidForecastEngine.ts — Non-paid revenue forecast pipeline
//   ├── combinationEngine.ts  — Slider modulators + combined charts
//   └── index.ts              — This barrel file
//
// Data flow:
//   Historical data (transactions)
//       │
//       ├──▶ roasForecastEngine  ──▶  per-source ROAS forecasts
//       │                              + combined ROAS (raw)
//       │
//       └──▶ nonPaidForecastEngine ──▶ per-source organic forecasts
//
//       ──▶ combinationEngine ──▶ slider-modulated combined ROAS
//                                 + slider-modulated total revenue
// ============================================================

// Types
export type { EvolutionFn, EvolutionConfig } from './types';
export type { DataQuality, JitterProfile } from './types';
export type { RoasPoint, NonPaidPoint, RawForecastPoint, ForecastChartPoint } from './types';
export type { RoasSourceForecast, RoasForecastResult } from './types';
export type { NonPaidSourceForecast, NonPaidForecastResult } from './types';
export type { ExpensePoint, ExpenseSourceForecast, ExpenseForecastResult } from './types';
export type { JournalOperation } from './types';
export { QUALITY_COLORS } from './types';

// Constants
export { NON_PAID_EVOLUTION, getEvolutionConfig, SOURCE_PALETTE } from './constants';
export { MARKETING_PLATFORMS, SOURCE_COLORS } from './constants';

// Math Utilities
export { seededRandom, normalRandom } from './math';

// Adaptive Jitter
export { computeAdaptiveJitter } from './adaptiveJitter';

// Forecast Engines
export { computeRoasForecast } from './roasForecastEngine';
export { computeNonPaidForecast } from './nonPaidForecastEngine';
export { computeUnknownSourceForecast } from './unknownForecastEngine';
export { computeExpenseForecast } from './expenseForecastEngine';
export { calculateRevenueSourceBreakdown, calculatePlatformAttribution } from './revenueSourceBreakdown';

// Combination Engine
export {
    applyRoasModulators,
    applyNonPaidModulators,
    computeModulatedCombinedRoas,
    computeModulatedTotalRevenue,
    computeModulatedCombinedNonPaid,
    computeRevenueForecast,
    computeStatisticalAccuracy,
    computeJournalCharts,
    computeSpecCharts,
    buildDailyContext,
    projectJournalOperations
} from './combinationEngine';

export type { JournalChart, SpecChart } from './combinationEngine';

export { ProductExplorer } from './ProductExplorer';
export { RevenueSubSourceBreakdown } from './revenueSubSourceBreakdown';
export { RevenueSourceBreakdownUI } from './RevenueSourceBreakdownUI';
export type { RevenueSourceSegment } from './RevenueSourceBreakdownUI';
export { SourceIcon } from './SourceIcon';
